/**
 * Automations — spec §5-F16, §8.1 `/settings/automations`.
 *
 * Rules are trigger → conditions → actions. The starter rules ship disabled, so
 * the common case is "turn this one on", not "build one from scratch" — the
 * list leads with that, and the builder is behind an explicit New rule.
 */

import { useState } from 'react';
import { Loader2, Play, Plus, Trash2, Workflow, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError, api } from '@/lib/api';
import { useProjects } from '@/features/projects';

interface AutomationView {
  _id: string;
  name: string;
  description?: string;
  isEnabled: boolean;
  projectId?: string | null;
  trigger: { type: string; config?: Record<string, unknown> };
  conditions: { field: string; operator: string; value: unknown }[];
  actions: { type: string; config?: Record<string, unknown> }[];
  runCount?: number;
  lastRunAt?: string;
  errorCount?: number;
}

interface AutomationRun {
  _id: string;
  automationId: string;
  status: 'success' | 'failed' | 'skipped';
  startedAt: string;
  durationMs?: number;
  error?: string;
  actionsRun?: number;
}

const TRIGGERS = [
  { value: 'task.created', label: 'Task created' },
  { value: 'task.status_changed', label: 'Status changed' },
  { value: 'task.priority_changed', label: 'Priority changed' },
  { value: 'task.assignee_changed', label: 'Assignee changed' },
  { value: 'task.overdue', label: 'Task overdue' },
  { value: 'task.blocked', label: 'Task blocked' },
  { value: 'comment.created', label: 'Comment added' },
  { value: 'subtasks.completed', label: 'All subtasks done' },
  { value: 'schedule', label: 'On a schedule' },
];

const ACTIONS = [
  { value: 'set_status', label: 'Set status' },
  { value: 'set_priority', label: 'Set priority' },
  { value: 'assign', label: 'Assign to someone' },
  { value: 'add_label', label: 'Add a label' },
  { value: 'remove_label', label: 'Remove a label' },
  { value: 'set_due_date', label: 'Set due date' },
  { value: 'add_comment', label: 'Add a comment' },
  { value: 'notify', label: 'Notify someone' },
  { value: 'create_task', label: 'Create a linked task' },
  { value: 'archive', label: 'Archive' },
];

function useAutomations() {
  return useQuery({
    queryKey: ['automations'],
    queryFn: async () => (await api.get<AutomationView[]>('/automations')).data,
  });
}

function useAutomationRuns(id?: string) {
  return useQuery({
    queryKey: ['automation-runs', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<AutomationRun[]>(`/automations/${id}/runs`)).data,
  });
}

function useSaveAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id?: string; patch: Record<string, unknown> }) =>
      id
        ? (await api.patch<AutomationView>(`/automations/${id}`, patch)).data
        : (await api.post<AutomationView>('/automations', patch)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

function useDeleteAutomation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.delete(`/automations/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['automations'] }),
  });
}

function RunsDialog({ rule, onClose }: { rule: AutomationView | null; onClose: () => void }) {
  const { data: runs = [], isLoading } = useAutomationRuns(rule?._id);

  return (
    <Dialog open={Boolean(rule)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogTitle className="mb-3 text-[16px] font-semibold">{rule?.name} — runs</DialogTitle>
        {isLoading ? (
          <Skeleton className="h-24" />
        ) : runs.length === 0 ? (
          <p className="text-[13px] vt-muted">
            This rule has not fired yet. Every execution is logged here with what it did and how long
            it took.
          </p>
        ) : (
          <ul className="max-h-80 divide-y divide-[var(--border)] overflow-y-auto">
            {runs.map((run) => (
              <li key={run._id} className="flex items-center gap-2 py-2 text-[12px]">
                <Badge tone={run.status === 'success' ? 'accent' : run.status === 'failed' ? 'danger' : 'neutral'}>
                  {run.status}
                </Badge>
                <span className="flex-1 truncate vt-muted">
                  {new Date(run.startedAt).toLocaleString()}
                  {run.error ? ` — ${run.error}` : ''}
                </span>
                {run.durationMs != null && <span className="vt-mono">{run.durationMs}ms</span>}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BuilderDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const save = useSaveAutomation();
  const { data: projects = [] } = useProjects();
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [trigger, setTrigger] = useState(TRIGGERS[0].value);
  const [action, setAction] = useState(ACTIONS[0].value);
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="mb-3 text-[16px] font-semibold">New rule</DialogTitle>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            if (!name.trim()) {
              setError('Give the rule a name so it is recognisable in the list');
              return;
            }
            try {
              await save.mutateAsync({
                patch: {
                  name: name.trim(),
                  trigger: { type: trigger, config: {} },
                  conditions: [],
                  actions: [{ type: action, config: {} }],
                  isEnabled: false,
                  ...(projectId ? { projectId } : {}),
                },
              });
              toast.success('Rule created — disabled until you turn it on');
              onOpenChange(false);
              setName('');
            } catch (err) {
              setError(
                err instanceof ApiError ? (err.details?.[0]?.message ?? err.message) : 'Could not save',
              );
            }
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-name">Name</Label>
            <Input id="rule-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-scope">Scope</Label>
            <select
              id="rule-scope"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
            >
              <option value="">Whole workspace</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.icon} {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-trigger">When</Label>
            <select
              id="rule-trigger"
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
            >
              {TRIGGERS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rule-action">Then</Label>
            <select
              id="rule-action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
            >
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            <p className="text-[11px] vt-muted">
              Conditions and action details are edited on the rule once it exists.
            </p>
          </div>

          {error && (
            <p role="alert" className="text-[12px] text-[var(--danger)]">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
              Create rule
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AutomationsPage() {
  const { data: rules, isLoading } = useAutomations();
  const save = useSaveAutomation();
  const remove = useDeleteAutomation();
  const [builderOpen, setBuilderOpen] = useState(false);
  const [runsFor, setRunsFor] = useState<AutomationView | null>(null);

  const triggerLabel = (type: string): string =>
    TRIGGERS.find((t) => t.value === type)?.label ?? type;

  return (
    <>
      <PageHeader
        title="Automations"
        subtitle="When this happens, if these are true, do these things."
        actions={
          <Button variant="primary" size="sm" data-testid="new-rule" onClick={() => setBuilderOpen(true)}>
            <Plus />
            New rule
          </Button>
        }
      />

      <div className="p-4">
        {isLoading ? (
          <Skeleton className="h-32" />
        ) : (rules ?? []).length === 0 ? (
          <EmptyState
            icon={<Workflow className="size-6" />}
            title="No rules yet"
            description="Automate the bookkeeping — assign reviewers, escalate blocked work, chase overdue tasks."
            action={
              <Button variant="primary" onClick={() => setBuilderOpen(true)}>
                <Plus />
                Create the first rule
              </Button>
            }
          />
        ) : (
          <div className="vt-surface overflow-hidden" data-testid="automations-list">
            {(rules ?? []).map((rule) => (
              <div
                key={rule._id}
                className="flex items-center gap-3 border-b border-[var(--border)] px-3 py-2.5 last:border-0"
              >
                <Zap
                  className="size-4 shrink-0"
                  style={{ color: rule.isEnabled ? 'var(--accent)' : 'var(--text-muted)' }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[13px] font-medium">{rule.name}</span>
                    {!rule.isEnabled && <Badge tone="neutral">off</Badge>}
                    {(rule.errorCount ?? 0) > 0 && <Badge tone="danger">{rule.errorCount} errors</Badge>}
                  </div>
                  <div className="text-[12px] vt-muted">
                    When {triggerLabel(rule.trigger.type)} → {rule.actions.length} action
                    {rule.actions.length === 1 ? '' : 's'}
                    {rule.conditions.length > 0
                      ? ` · ${rule.conditions.length} condition${rule.conditions.length === 1 ? '' : 's'}`
                      : ''}
                    {rule.runCount ? ` · ran ${rule.runCount}×` : ''}
                  </div>
                </div>

                <Button variant="ghost" size="sm" onClick={() => setRunsFor(rule)}>
                  <Play />
                  Runs
                </Button>

                <label className="flex shrink-0 items-center gap-1.5 text-[12px]">
                  <input
                    type="checkbox"
                    aria-label={`${rule.isEnabled ? 'Disable' : 'Enable'} ${rule.name}`}
                    checked={rule.isEnabled}
                    onChange={async () => {
                      await save.mutateAsync({ id: rule._id, patch: { isEnabled: !rule.isEnabled } });
                      toast.success(`${rule.name} ${rule.isEnabled ? 'disabled' : 'enabled'}`);
                    }}
                  />
                  {rule.isEnabled ? 'On' : 'Off'}
                </label>

                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Delete ${rule.name}`}
                  onClick={async () => {
                    if (!window.confirm(`Delete “${rule.name}”?`)) return;
                    await remove.mutateAsync(rule._id);
                    toast.message('Rule deleted');
                  }}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <BuilderDialog open={builderOpen} onOpenChange={setBuilderOpen} />
      <RunsDialog rule={runsFor} onClose={() => setRunsFor(null)} />
    </>
  );
}
